// Repo readiness data layer (arc 101 slice 12 step 3) — the dashboard's half of
// the platform's ONE readiness predicate.
//
// `next_steps` (`packages/mcp/src/tools.ts`) is that predicate: the ordered
// ladder of preconditions between a credential and a first `plan_start` scout
// call, each rung carrying the fact it was decided from and WHERE it clears.
// `GET /v1/next-steps` is a second DOOR onto the same implementation, not a
// second implementation — so this module is a CLIENT of that answer and nothing
// here re-derives a rung, a remedy or a verdict.
//
// That distinction is the whole reason this file exists. What it replaced was a
// genuine second implementation: RepoDetail's `ai_first_at === null` banner,
// which re-derived the `repo_onboarded` rung in the browser and kept a
// byte-for-byte copy of `TASK_STALL_REMEDY[RepoNotOnboarded]` in
// `locales/en.json`, pinned by a test because the copy could otherwise drift.
// The server's own rung says that sentence AND the refinement no browser can
// make — "a setup pass is already running, do not mint a second" — so the banner
// is deleted rather than kept beside the ladder. Two answers to "is this repo
// ready" that can disagree are worse than none.

import type { ReadinessStepId } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

// THE RUNG VOCABULARY IS NOT HERE. `READINESS_STEPS` / `ReadinessStepId` live in
// `@developerz/domain` (`value-objects/readiness-step.ts`), which is the one
// address the Hono server serving `/v1/next-steps` and this CDN-served SPA can
// both hold — `packages/mcp`, which builds `NextStepIdSchema` from that same
// array, is not a dashboard dependency and never will be.
//
// This module RESTATED it until arc 101 slice 10, and a restated enum under a
// DYNAMIC label key (`t(`readiness.step_${rung.step}`)`, `ReadinessLadder.tsx`)
// is a rung that renders as its own key: the route grew `box_attached` and
// `mcp_connected` (#3632) and `plan_selected` (#4611), the array did not, and a
// live screen showed `⟦readiness.step_box_attached⟧`. The label-coverage test in
// `components/RepoReadiness.test.tsx` could see none of it, because it graded the
// route against THAT SAME ARRAY — a copy checked against itself. It now imports
// the vocabulary from the domain, so a rung the route can serve without a label
// here is a RED TEST rather than a copy agreeing with itself.
//
// `t()` takes a `string` key (`packages/i18n/src/translator.ts`) and this panel
// has no label MAP, so that test is the whole guard — TypeScript cannot reach a
// key built from a template literal.
//
// `READINESS_STATUSES` and `READINESS_CLEARED_BY` below are still dashboard-local
// restatements of wire enums, graded by that same test against themselves. They
// are narrower (a status and a destination, neither of which has grown since it
// was written) and they have never drifted, so they stay here for now.

/**
 * `unknown` is a POSITIVE answer — "this rung was not checked, and `observed`
 * says why": the session's credential does not hold the read scope that rung
 * rides. It is neither a failure nor a loading state, and it normally keeps
 * `ready` false, because the answer never claims readiness it did not verify.
 * ONE waiver, decided by the server and never here (#4263): a credential that
 * can already dispatch (`write:tasks`, `read:tasks`, `read:repos`,
 * `read:runners`) is not held un-ready by a fact it could not READ — the rung
 * stays `unknown` and its `observed` says why it is not in the way. This panel
 * renders the `ready` it is served and derives none of it.
 */
export const READINESS_STATUSES = ['ok', 'blocked', 'unknown'] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number];

/**
 * WHERE a rung clears — a destination, never a severity, and the field that
 * exists so seven unrelated answers are not collapsed into one absent verb.
 * Only `here` is actionable by an agent over MCP; the rest name a different
 * surface each, and the panel gives every one its own sentence.
 */
export const READINESS_CLEARED_BY = [
  'here',
  'operator',
  'browser',
  'rest',
  'earlier_rung',
  'in_flight',
  'none',
] as const;
export type ReadinessClearedBy = (typeof READINESS_CLEARED_BY)[number];

/** One rung as `GET /v1/next-steps` serves it. */
export type ApiReadinessStep = {
  readonly step: ReadinessStepId;
  readonly status: ReadinessStatus;
  /** The fact behind the verdict, present on EVERY rung including the passing ones. */
  readonly observed: string;
  readonly cleared_by: ReadinessClearedBy;
  /** An MCP verb — non-null EXACTLY when `cleared_by` is `here`, never otherwise. */
  readonly verb: string | null;
  /** What to do next on this rung. A server fact; it renders as-is. */
  readonly detail: string;
};

/**
 * The ladder. `steps` arrives in DEPENDENCY order and is rendered in the order
 * received — never re-sorted, and never filtered down to the failures, which is
 * what lets a reader see the whole remaining path instead of discovering it one
 * refusal at a time.
 */
export type ApiReadiness = {
  /** Echoed back; `null` when the ladder answered for the ACCOUNT. */
  readonly repo: string | null;
  /** True when no rung BLOCKS — see {@link READINESS_STATUSES} on `unknown`. */
  readonly ready: boolean;
  /** The first BLOCKING rung; `null` exactly when `ready`. */
  readonly next: ReadinessStepId | null;
  readonly steps: readonly ApiReadinessStep[];
};

export const READINESS_QUERY_KEY = 'readiness';

/**
 * One repo's ladder. `fullName` is the plain `owner/repo` the route takes — the
 * same alphabet `plan_start` accepts, so the repo this clears is the repo the
 * plan then starts on — percent-encoded here as a query VALUE (the slash is
 * data, not a path separator). A name the route cannot parse is a 400, and the
 * boundary renders the API's own refusal rather than one guessed at here.
 */
export function useRepoReadiness(fullName: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<ApiReadiness>(() => ({
    queryKey: [READINESS_QUERY_KEY, fullName(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<ApiReadiness>(`/v1/next-steps?repo=${encodeURIComponent(fullName())}`, token),
  }));
}

/**
 * The ACCOUNT ladder — the same route with no `repo`, which answers "can this
 * account start?" over the rungs that are not per-repo.
 *
 * WHY IT EXISTS. The onboarding funnel and this ladder are two different
 * questions, and until this the dashboard only ever asked one of them. The
 * funnel tracks WHICH WIZARD STEPS THE USER WALKED (`Account.onboardingState`,
 * a persisted column advanced by the wizard's own buttons); the ladder grades
 * WHETHER THE ACCOUNT CAN ACTUALLY RUN WORK, read live off keys, installations,
 * repos and boxes. They are not the same fact and they routinely disagree: an
 * account reaches the funnel's terminal `active` by clicking through every
 * card, while the ladder still reports `scout_box` blocked because no machine
 * was ever enrolled. The wizard then said "You are all set" to an account on
 * which nothing can run, and the first thing the user would have learned
 * otherwise was a `no_scout_capacity` refusal from their agent.
 *
 * So this is deliberately NOT a second progress model, and nothing here grades
 * anything: `GET /v1/next-steps` is a door onto `resolveReadiness`, the same
 * implementation `next_steps` serves over MCP, and this module is a client of
 * that answer. Two answers to "is this account ready" that can disagree are
 * worse than none — which is the rule the repo-scoped half of this file already
 * states, applied to the account scope it left unasked.
 *
 * No `refetchInterval`: the surfaces that mount it (the wizard's terminal card,
 * Home) refetch on focus like every other query here, and the rungs clear on
 * human-speed acts — enrolling a box, verifying a key — not on a timer the SPA
 * should be spinning.
 */
export function useAccountReadiness(token: string | null = SESSION_TOKEN) {
  return createQuery<ApiReadiness>(() => ({
    queryKey: [READINESS_QUERY_KEY, null, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<ApiReadiness>('/v1/next-steps', token),
  }));
}
